/* Number, money and date formatting.
 *
 * Formatting is deliberately locale-independent — space thousands separator,
 * dot decimal, trailing currency code: `10 080.00 USD`. This is not a cosmetic
 * choice. Russian and English disagree about separators, and an ambiguous
 * number in an invoicing product is a live source of error. It also matches how
 * ASA already writes figures in the Master File and in their debit notes.
 * (DESIGN.md § Typography, ARCHITECTURE-SPINE § Consistency Conventions.)
 *
 * Amounts arrive from the API as decimal STRINGS, never JSON numbers — the
 * backend stores money as shopspring/decimal and serialises it as a string so
 * no value passes through a float (AD-9). Parsing to a JS number here is for
 * display only; never compute money client-side.
 */

/* U+202F NARROW NO-BREAK SPACE is the group separator, not a plain space.
 * Two reasons, and the first is a bug we actually hit: under
 * `font-variant-numeric: tabular-nums` a regular or non-breaking space is
 * advanced to a full DIGIT width, so `412 800` renders with a gap wide enough
 * to read as two separate figures. The narrow form keeps its own width. It is
 * also the correct typographic mark for digit grouping, and like NBSP it does
 * not wrap — a money value must never break across lines. */
const GROUP_SEP = '\u202f'

/** Separates the currency code from the amount. Full width, still unbreakable. */
const NBSP = '\u00a0'

function group(integerDigits: string): string {
  return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP)
}

export function formatNumber(
  value: number | string | null | undefined,
  fractionDigits = 0,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(n)) return '—'

  const negative = n < 0
  const [whole = '0', fraction] = Math.abs(n).toFixed(fractionDigits).split('.')
  const body = fraction ? `${group(whole)}.${fraction}` : group(whole)

  // U+2212 MINUS SIGN, not a hyphen: it aligns with the digits in tabular
  // figures, where a hyphen sits too high and too short.
  return negative ? `−${body}` : body
}

/**
 * `10 080.00 USD`. The currency code trails the figure and is rendered as part
 * of the string; components that want the code in a muted weight should use
 * `splitMoney` instead so they can style the two halves separately.
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency = 'USD',
  fractionDigits = 2,
): string {
  const amount = formatNumber(value, fractionDigits)
  return amount === '—' ? amount : `${amount}${NBSP}${currency}`
}

/** The two halves of a money value, for the common "big figure, muted code" tile. */
export function splitMoney(
  value: number | string | null | undefined,
  currency = 'USD',
  fractionDigits = 2,
): { amount: string; currency: string } {
  return { amount: formatNumber(value, fractionDigits), currency }
}

/** Percentages carry one decimal, and a gain carries an explicit sign. */
export function formatPercent(
  value: number | string | null | undefined,
  { signed = false }: { signed?: boolean } = {},
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(n)) return '—'
  const sign = signed && n > 0 ? '+' : ''
  return `${sign}${formatNumber(n, 1)}%`
}

/* ── Dates ─────────────────────────────────────────────────────────────────
 * Shipment milestones, expected payment dates and validity dates are date-only
 * values with no time and no timezone (AD-19) — an ISO `YYYY-MM-DD` string. We
 * parse them as UTC and format as UTC, because reading `2026-07-27` through a
 * local-time constructor silently shifts the day west of Greenwich. Event
 * timestamps are separate: those are real instants and render in the viewer's
 * zone.
 */

/** Parses an API date-only string (`YYYY-MM-DD`) without a timezone shift. */
export function parseDateOnly(value: string): Date {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
}

export function formatDate(
  value: string | null | undefined,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseDateOnly(value))
}

/** `12 Jul` — for a row of dates inside one shipment, where the full form
 *  would state the same year four times and drown the days in it. Anything
 *  that stands alone keeps `formatDate`; a bare day-month read out of context
 *  is how a January date gets filed under the wrong year. */
export function formatDateShort(
  value: string | null | undefined,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(parseDateOnly(value))
}

export function formatDateLong(
  value: string | null | undefined,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseDateOnly(value))
}

/** An event instant (timestamptz, stored UTC) rendered in the viewer's zone. */
export function formatTimestamp(
  value: string | null | undefined,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))
}

export function formatTime(
  value: string | null | undefined,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))
}

/** The unit strings `formatRelativeTime` needs, supplied by the caller from the
 *  active catalogue (`common.relativeTime`). Passing them in keeps this module
 *  free of an i18next import — it stays a pure formatting layer that a test can
 *  call without booting an i18n instance. */
export interface RelativeTimeLabels {
  now: string
  minutes: (count: number) => string
  hours: (count: number) => string
  days: (count: number) => string
}

const EN_RELATIVE: RelativeTimeLabels = {
  now: 'now',
  minutes: (n) => `${n} min`,
  hours: (n) => `${n} h`,
  days: (n) => `${n} d`,
}

/**
 * A short relative age for event feeds: `now`, `12 min`, `4 h`, `3 d`.
 *
 * Deliberately terse and unitful rather than conversational. "2 hours ago"
 * costs a third of a notification row's width to say what "4 h" says, and the
 * feed is scanned down its right edge as a column of ages, not read as
 * sentences. Anything past a week falls back to an absolute date, because
 * "63 d" is arithmetic the reader should not have to do.
 *
 * Russian units are equally terse («мин», «ч», «д») so the column keeps its
 * width in both languages. Prefer the `useRelativeTime` hook in components; the
 * labels default to English only so a non-React caller still works.
 */
export function formatRelativeTime(
  value: string | null | undefined,
  now: Date = new Date(),
  labels: RelativeTimeLabels = EN_RELATIVE,
  locale = 'en-GB',
): string {
  if (!value) return '—'
  const then = new Date(value)
  const ms = then.getTime()
  if (!Number.isFinite(ms)) return '—'

  const minutes = Math.round((now.getTime() - ms) / 60_000)

  // A clock skewed a minute or two ahead of the server must not render "in
  // 2 min" on an event that has already happened.
  if (minutes < 1) return labels.now
  if (minutes < 60) return labels.minutes(minutes)

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return labels.hours(hours)

  const days = Math.floor(hours / 24)
  if (days < 7) return labels.days(days)

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(then)
}

/** Initials for an avatar. Two letters at most. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}
